import { Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { FieldTypes } from '../general-popover-settings.dto';
import { firstValueFrom, map, take } from 'rxjs';
import { EditTemplateFacade } from 'app/main/pages/edit-template/store/edit-template-store.facade';
import { UserSettingsFacade } from 'app/store/user-settings.facade';
import { ProfileService } from 'app/main/pages/profile-settings/profile.service';
import { GlobalService } from 'app/global/global.service';
import { IntegrationService } from 'app/global/integrations/integrations.service';

@Injectable({
    providedIn: 'root'
})
export class UserFieldsService {
    constructor(
        private globalService: GlobalService,
        private _translocoService: TranslocoService,
        private editTemplateFacade: EditTemplateFacade,
        private _userSettingsFacade$: UserSettingsFacade,
        private _profileService: ProfileService,
        private integrationService: IntegrationService,
    ) { }
    public userSettings$ = this._userSettingsFacade$._userSettings$;
    async getAllFieldsAsync(): Promise<FieldTypes[]> {
        const senderFields = await this.getUserFields();

        return firstValueFrom(
            this.editTemplateFacade.template$.pipe(
                take(1),
                map(template => {
                    template?.custom_fields?.forEach(field => {
                        if(field?.meta_data){
                            senderFields.push({
                                id: field?.meta_data_id.toString(),
                                name: field?.meta_data?.meta_data_name,
                                value: field?.value ?? '',
                                field_type: this._translocoService.translate("Field setting sender text (Template/Document - section) - Sender"),
                                field_info: 'me',
                                prefixid: field?.meta_data_id.toString(),
                                field_section: "user_field"
                            })
                        }
                    })
                    return [...senderFields];
                })
            )
        );
    }

    async refreshUserFieldsInElements() {
        const senderFields = await this.userData();
        if (!senderFields) {
            return null;
        }
        const senderInformation: Record<'full_name' | 'email' | 'phone_no' | 'address' | 'zip_code' | 'city' | 'organization' | 'reg.no', {value :string, id:string}> = {
            full_name: {value :senderFields?.name ?? '' , id : `me1`},
            email: {value:senderFields?.email ?? '', id : `me3`},
            phone_no: {value:senderFields?.phone ?? '', id: `me4`},
            address : {value:senderFields?.address ?? '', id: `me6`},
            zip_code : {value:senderFields?.zipcode ?? '', id : `me7`},
            city : {value:senderFields?.city ?? '', id : `me8`},
            organization : {value:senderFields?.organization ?? '', id: `me9`},
            'reg.no' : {value:senderFields?.registration_number ?? '', id: `me10`},
        };
        for (const key in this.globalService.fields_data) {
            if (this.globalService.fields_data[key]) {
                const field = this.globalService.fields_data[key];
                if(field.field_info === "me"){
                    const senderInfo = Object.values(senderInformation).find(key => key.id === field.prefixid || `${key.id}${senderFields?.id}` === field.prefixid) || senderInformation[field.selected_type];
                    this.globalService.updateField(field.uuid, {prefixid:senderInfo?.id, selected_field_type_id: senderInfo?.id});
                    this.integrationService.handleTextField(field, (senderInfo?.value || ''));
                }
            }
        }
    }

    /**
     * Get default user/sender fields
     */
    async getUserFields(): Promise<FieldTypes[]> {
        const sender = await this.userData();
        return [
                { id: '0', nameKey: 'Content new field (Template/Document - section) - Field name - New field', value: '', fieldTypeKey: 'Field setting new text (Template/Document - section) - New', field_section: 'custom', field_info: 'custom_field' },
                { id: 'me1', nameKey: 'Full name (general) - Full name', value: sender?.name, fieldTypeKey: 'Field setting sender text (Template/Document - section) - Sender', field_section: 'user_field', field_info: 'me' },
                { id: 'me3', nameKey: 'Email (general) - Email', value: sender?.email, fieldTypeKey: 'Field setting sender text (Template/Document - section) - Sender', field_section: 'user_field', field_info: 'me' },
                { id: 'me4', nameKey: 'Phone no (general) - Phone no', value: sender?.phone, fieldTypeKey: 'Field setting sender text (Template/Document - section) - Sender', field_section: 'user_field', field_info: 'me' },
                { id: 'me6', nameKey: 'Address (general) - Address', value: sender?.address, fieldTypeKey: 'Field setting sender text (Template/Document - section) - Sender', field_section: 'user_field', field_info: 'me' },
                { id: 'me7', nameKey: 'Zip code (general) - Zip code', value: sender?.zipcode, fieldTypeKey: 'Field setting sender text (Template/Document - section) - Sender', field_section: 'user_field', field_info: 'me' },
                { id: 'me8', nameKey: 'City (general) - City', value: sender?.city, fieldTypeKey: 'Field setting sender text (Template/Document - section) - Sender', field_section: 'user_field', field_info: 'me' },
                { id: 'me9', nameKey: 'Organization (general) - Organization', value: sender?.organization, fieldTypeKey: 'Field setting sender org (Template/Document - section) - Sender Org', field_section: 'user_field', field_info: 'me' },
                { id: 'me10', nameKey: 'Reg no (general) - Reg no', value: sender?.registration_number, fieldTypeKey: 'Field setting sender org (Template/Document - section) - Sender Org', field_section: 'user_field', field_info: 'me' },
            ].map(def => ({
                id: def.id,
                name: this._translocoService.translate(def.nameKey),
                value: def.value ?? '',
                field_type: this._translocoService.translate(def.fieldTypeKey),
                field_info: def.field_info,
                prefixid: def.id,
                field_section: def.field_section
            }));
    }

    private async userData () {
        const user_settings = await firstValueFrom(this.userSettings$.pipe(take(1)));
        if (!user_settings) {
            return null;
        }
        const defaultWordSpace = user_settings?.workspaces.find(x => x?.default === true);
        return this._profileService.buildUserProfileData(user_settings, defaultWordSpace);
    }
}
